// Named export: the default export of src/store is the Quasar store
// FACTORY, not the live instance. Importing the default here makes every
// `store.state.main...` read below throw "Cannot read properties of
// undefined (reading 'main')" at render time.
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { isEmpty } from "lodash";
import { diff } from "deep-object-diff";

// Access tiers in ascending order of restriction. The tier on a top-level
// folder governs all files/subfolders beneath it. Labels are TYPE labels
// (Title Case, translated at render time) — not statuses.
//
// The "personal" tier is special: tier-level access is granted to any
// employee, but per-document reads/writes are gated to the owner only via
// Firestore + Storage rules. Admins do NOT see other users' personal files.
export const LIBRARY_TIERS = {
  public: {
    label: "Public",
    icon: "library",
    description: "Any employee can read and upload",
  },
  accounting: {
    label: "Accounting",
    icon: "accounting",
    description: "Accounting role, management, or admin",
  },
  management: {
    label: "Management",
    icon: "management",
    description: "Management role or admin",
  },
  admin: {
    label: "Admin",
    icon: "admin",
    description: "Admin/super only",
  },
  personal: {
    label: "My Files",
    icon: "user",
    description: "Visible only to you; 1 GB cap",
  },
};
deepFreeze(LIBRARY_TIERS);

export const LIBRARY_TIER_KEYS = Object.keys(LIBRARY_TIERS);

// Per-user storage cap for the personal tier (1 GB).
export const PERSONAL_LIBRARY_QUOTA_BYTES = 1024 * 1024 * 1024;

// Sum the bytes of a user's active (non-trashed) personal-tier files. The
// `files` argument should be the libraryFiles state map.
export function personalLibraryUsageBytes(files, userID) {
  if (!userID) return 0;
  let total = 0;
  Object.values(files || {}).forEach((f) => {
    if (
      f.data.tier === "personal" &&
      f.data.createdBy === userID &&
      !f.data.deletedAt
    ) {
      total += Number(f.data.size) || 0;
    }
  });
  return total;
}

export const LIBRARY_FOLDER_FIELDS = {
  name: "",
  parentID: null,
  tier: "public",
  description: "",
  // Read-only folders — the migrated Dropbox job folders. Every employee
  // with tier access can open and download what is inside, but only an
  // administrator can add, rename, move or delete anything. The flag is
  // denormalised onto every folder in the subtree (SAVE_LIBRARY_FOLDER
  // cascades it; the migration script writes it on every folder it
  // creates) so Firestore rules can read it without walking ancestors.
  readOnly: false,
  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(LIBRARY_FOLDER_FIELDS);

// ---------------------------------------------------------------------------
// Tier access checks. These mirror the server-side rules in storage.rules
// and firestore.rules so the UI hides what the user cannot load anyway.
// ---------------------------------------------------------------------------

export function canAccessTier(user, tier) {
  if (!user) return false;
  // Personal tier is opened up to any employee at the tier level so they
  // can list their own folders/files; per-doc ownership is enforced by
  // canAccessFolder/canAccessFile and by Firestore + Storage rules.
  if (tier === "personal") return Boolean(user.isEmployee);
  if (user.isSuper) return true;
  switch (tier) {
    case "public":
      return Boolean(user.isEmployee);
    case "accounting":
      return Boolean(
        user.isEmployee &&
          (user.hasRole("accounting") || user.hasRole("management"))
      );
    case "management":
      return Boolean(user.isEmployee && user.hasRole("management"));
    case "admin":
      return false; // only isSuper covered above
    default:
      return false;
  }
}

export function canWriteToTier(user, tier) {
  // Currently read and write share the same gate. Separated so we can
  // tighten write-access later without touching read callers.
  return canAccessTier(user, tier);
}

// Works for both class instances (`folder.data.tier`) and the plain
// `{ id, data }` shells the page builds for folders it has just created.
function fieldOf(entity, key) {
  if (!entity) return undefined;
  return entity.data ? entity.data[key] : entity[key];
}

// Read-only is inherited: a folder is read-only when its own flag is set or
// any ancestor's is. The flag is denormalised down the tree on write, so the
// ancestor walk is a safety net for a subtree the cascade never reached.
export function isFolderReadOnly(folder) {
  if (!folder) return false;
  if (folder instanceof LibraryFolder) return folder.isReadOnly;
  return Boolean(fieldOf(folder, "readOnly"));
}

// Per-document access for folders. Adds the personal-tier ownership check
// on top of the tier-level gate.
export function canAccessFolder(user, folder) {
  if (!user || !folder) return false;
  const tier = fieldOf(folder, "tier");
  if (!canAccessTier(user, tier)) return false;
  if (tier === "personal") {
    return fieldOf(folder, "createdBy") === user.id;
  }
  return true;
}

// Writing a folder covers renaming/moving/deleting it AND adding files or
// subfolders to it. A read-only folder is writable by administrators only.
export function canWriteFolder(user, folder) {
  if (!canAccessFolder(user, folder)) return false;
  if (!canWriteToTier(user, fieldOf(folder, "tier"))) return false;
  return !isFolderReadOnly(folder) || Boolean(user.isAdmin);
}

// Per-document access for files. Mirrors canAccessFolder.
export function canAccessFile(user, file) {
  if (!user || !file) return false;
  const tier = fieldOf(file, "tier");
  if (!canAccessTier(user, tier)) return false;
  if (tier === "personal") {
    return fieldOf(file, "createdBy") === user.id;
  }
  return true;
}

// Tier + ownership only. Read-only containment is checked by the file class,
// which knows its folder.
export function canWriteFile(user, file) {
  return (
    canAccessFile(user, file) && canWriteToTier(user, fieldOf(file, "tier"))
  );
}

export function accessibleTiers(user) {
  return LIBRARY_TIER_KEYS.filter((tier) => canAccessTier(user, tier));
}

export default class LibraryFolder extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, LIBRARY_FOLDER_FIELDS);

    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.readOnly = Boolean(data.readOnly);

    if (!LIBRARY_TIER_KEYS.includes(data.tier)) {
      data.tier = "public";
    }

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: LibraryFolder.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      lock: this.canBeLockedBy,
    });
  }

  // ------------------------------------------------------------------
  // Permissions
  // ------------------------------------------------------------------

  static canBeCreatedBy(user) {
    // Anyone who can access at least one tier can create a folder in it.
    return Boolean(user && user.isEmployee && accessibleTiers(user).length);
  }

  // Creating a folder at a specific spot: inside `parent`, or at the root of
  // `tier` when there is no parent.
  static canBeCreatedIn(user, parent, tier) {
    if (!LibraryFolder.canBeCreatedBy(user)) return false;
    if (parent) return canWriteFolder(user, parent);
    return canWriteToTier(user, tier);
  }

  canBeEditedBy(user) {
    return canWriteFolder(user, this);
  }

  canBeDeletedBy(user) {
    return canWriteFolder(user, this);
  }

  canBeViewedBy(user) {
    return canAccessFolder(user, this);
  }

  // Only administrators may mark a folder read-only or lift the flag.
  canBeLockedBy(user) {
    return Boolean(
      user && user.isAdmin && canAccessFolder(user, this) && !this.isPersonal
    );
  }

  // ------------------------------------------------------------------
  // Database
  // ------------------------------------------------------------------

  get path() {
    return LibraryFolder.getPath(this.id);
  }

  static getPath(id) {
    let path = "libraryFolders";
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(folder) {
    return folder.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new LibraryFolder(snapshot.id, data);
  }

  // ------------------------------------------------------------------
  // Tree helpers
  // ------------------------------------------------------------------

  get isRoot() {
    return !this.data.parentID;
  }

  get isPersonal() {
    return this.data.tier === "personal";
  }

  get parent() {
    return this.data.parentID
      ? store.state.main.libraryFolders[this.data.parentID] || null
      : null;
  }

  get ancestors() {
    const chain = [];
    let current = this.parent;
    const seen = new Set();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      chain.unshift(current);
      current = current.parent;
    }
    return chain;
  }

  get breadcrumbs() {
    return [...this.ancestors, this];
  }

  get pathLabel() {
    return this.breadcrumbs.map((folder) => folder.data.name).join(" / ");
  }

  get isReadOnly() {
    if (this.data.readOnly) return true;
    return this.ancestors.some((ancestor) => ancestor.data.readOnly);
  }

  get tierInfo() {
    return LIBRARY_TIERS[this.data.tier] || LIBRARY_TIERS.public;
  }

  // ------------------------------------------------------------------
  // Validation
  // ------------------------------------------------------------------

  get rules() {
    return {
      name: [
        (a) => Boolean(a && a.trim().length) || "required",
        (a) => !/[\\/]/.test(a || "") || "folderNameInvalid",
      ],
      tier: [(a) => LIBRARY_TIER_KEYS.includes(a) || "required"],
    };
  }

  get isValid() {
    return Object.entries(this.rules).every(([key, rules]) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, LIBRARY_FOLDER_FIELDS)));
  }

  isEqual(other) {
    return Boolean(
      other && this.id === other.id && isEmpty(diff(this.data, other.data))
    );
  }

  get modifiedDate() {
    return this.data.modified || this.data.created || null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name;
  }
}
